/**
 * Zentrale Magazin-API von Rothus Medien.
 * Liefert nur veröffentlichte glide-Ausgaben, bereits absteigend sortiert.
 */
export const ROTHUS_MAGAZINES_URL =
  'https://thmyvcccuudbjjgnzpxz.supabase.co/functions/v1/magazines?brand=glide';

export const READER_EMBED_BASE = 'https://rothusmedien.ch/embed/reader';

export const readerSrc = (issue: MagazineIssue): string =>
  issue.embed_url ||
  `${READER_EMBED_BASE}/glide-${String(issue.issue_number).padStart(2, '0')}-${new Date(
    issue.publication_date,
  ).getFullYear()}`;

export interface RothusMagazine {
  id: string;
  slug: string;
  brand: string;
  title: string;
  /** Individueller Ausgabentitel, z.B. "Faszination Rollski" */
  issue_title?: string | null;
  description?: string | null;
  /** Optionale zentrale Homepage-Metadaten; keine lokalen Themen ergänzen. */
  teaser?: string | null;
  editorial_highlights?: string[] | null;
  status?: string | null;
  processing_status?: string | null;
  price_chf?: number | string | null;
  issue_number: string;
  published_at: string;
  cover_url: string | null;
  total_pages?: number | null;
  preview_pages?: number | null;
  full_access?: boolean | null;
  shop_url?: string | null;
  subscription_url?: string | null;
  reader_url?: string | null;
  embed_url?: string | null;
  digital_single_enabled?: boolean | null;
  digital_single_price_chf?: number | string | null;
  digital_purchase_url?: string | null;
}

/** Interne, im bestehenden Design verwendete Ausgaben-Form. */
export interface MagazineIssue {
  id: string;
  title: string;
  issue_number: string;
  cover_image_url: string | null;
  publication_date: string;
  description: string | null;
  price_chf: number;
  slug?: string;
  embed_url?: string | null;
  reader_url?: string | null;
  shop_url?: string | null;
  /** Nur gesetzt, wenn die zentrale API den Digitalverkauf freigibt. */
  editorial_title?: string | null;
  editorial_teaser?: string | null;
  editorial_highlights?: string[];
  current_issue_eligible?: boolean;
  digital_single_price_chf?: number | null;
  digital_purchase_url?: string | null;
}

/** Digitalverkauf nur zeigen, wenn die API alle drei Angaben liefert. */
export const hasDigitalSingle = (
  issue: MagazineIssue,
): issue is MagazineIssue & { digital_single_price_chf: number; digital_purchase_url: string } =>
  typeof issue.digital_single_price_chf === 'number' &&
  Number.isFinite(issue.digital_single_price_chf) &&
  issue.digital_single_price_chf > 0 &&
  !!issue.digital_purchase_url;

/** Standardpreis einer Einzelausgabe (nur Fallback, wenn die API keinen Preis liefert). */
export const DEFAULT_ISSUE_PRICE_CHF = 14.8;

/** CHF-Betrag im bestehenden Format ("14.80"). */
export const formatChf = (value: number): string => value.toFixed(2);


/**
 * Lokale Zusatzdaten (Titel/Beschreibung), die (noch) nicht zentral geliefert werden.
 * Zuordnung über slug bzw. issue_number.
 */
export const localIssueExtras: Record<string, { title: string; description: string }> = {
  'glide-04-2026': {
    title: 'Faszination Rollski',
    description:
      'Auf Rädern rasant durch den Sommer. Olympia-Nachlese mit Nadja Kälin und Nicola Wigger. Plus: Ski-, Schuh- und Textil-Neuheiten 2026/27.',
  },
  'glide-03-2026': {
    title: 'Erlebnis Engadiner',
    description:
      'Nadja Kälin – Sprung auf die grosse Langlaufbühne. Anton Giovanoli – Maestro der Seeloipen. Plus: Sportsonnenbrillen im Test.',
  },
  'glide-02-2025': {
    title: 'Langlaufen in der Stadt',
    description:
      'Wenn die St. Galler spuren: Urbanes Langlaufen und kulinarische Genussloipen im Engadin.',
  },
  'glide-01-2025': {
    title: 'Die Magie des ersten Schnees',
    description:
      'Interview mit Dario Cologna, Einsteiger-Tipps und die neuesten Ski-Trends 2025/2026.',
  },
};

/** Lokaler Fallback, falls die zentrale API kurzfristig nicht erreichbar ist. */
export const fallbackIssues: MagazineIssue[] = [
  {
    id: '4',
    title: 'Faszination Rollski',
    issue_number: '04',
    cover_image_url: '/magazines/cover-04-2026.jpg',
    publication_date: '2026-07-15',
    description: localIssueExtras['glide-04-2026'].description,
    price_chf: DEFAULT_ISSUE_PRICE_CHF,
    slug: 'glide-04-2026',
    embed_url: `${READER_EMBED_BASE}/glide-04-2026`,
    reader_url: 'https://rothusmedien.ch/reader/glide-04-2026',
    shop_url: '/shop#einzelausgaben',
  },
  {
    id: '3',
    title: 'Erlebnis Engadiner',
    issue_number: '03',
    cover_image_url: '/magazines/cover-03-2026.jpg',
    publication_date: '2026-02-01',
    description: localIssueExtras['glide-03-2026'].description,
    price_chf: DEFAULT_ISSUE_PRICE_CHF,
    slug: 'glide-03-2026',
    embed_url: `${READER_EMBED_BASE}/glide-03-2026`,
    reader_url: 'https://rothusmedien.ch/reader/glide-03-2026',
    shop_url: '/shop#einzelausgaben',
  },
  {
    id: '2',
    title: 'Langlaufen in der Stadt',
    issue_number: '02',
    cover_image_url: '/magazines/cover-02-2025.jpg',
    publication_date: '2025-12-15',
    description: localIssueExtras['glide-02-2025'].description,
    price_chf: DEFAULT_ISSUE_PRICE_CHF,
    slug: 'glide-02-2025',
    embed_url: `${READER_EMBED_BASE}/glide-02-2025`,
    reader_url: 'https://rothusmedien.ch/reader/glide-02-2025',
    shop_url: '/shop#einzelausgaben',
  },
  {
    id: '1',
    title: 'Die Magie des ersten Schnees',
    issue_number: '01',
    cover_image_url: '/magazines/cover-01-2025.jpg',
    publication_date: '2025-10-15',
    description: localIssueExtras['glide-01-2025'].description,
    price_chf: DEFAULT_ISSUE_PRICE_CHF,
    slug: 'glide-01-2025',
    embed_url: `${READER_EMBED_BASE}/glide-01-2025`,
    reader_url: 'https://rothusmedien.ch/reader/glide-01-2025',
    shop_url: '/shop#einzelausgaben',
  },
];

/** "4/26" -> "04" (bestehende glide-Darstellung beibehalten) */
export const formatIssueNumber = (raw: string): string => {
  const first = String(raw ?? '').split('/')[0].trim();
  return /^\d+$/.test(first) ? first.padStart(2, '0') : String(raw ?? '');
};

export const mapMagazine = (m: RothusMagazine): MagazineIssue => {
  const extras = localIssueExtras[m.slug];
  const apiPrice = typeof m.price_chf === 'string' ? Number(m.price_chf) : m.price_chf;
  const rawDigital =
    typeof m.digital_single_price_chf === 'string'
      ? Number(m.digital_single_price_chf)
      : m.digital_single_price_chf;
  const digitalEnabled =
    m.digital_single_enabled === true &&
    typeof rawDigital === 'number' &&
    Number.isFinite(rawDigital) &&
    rawDigital > 0 &&
    !!m.digital_purchase_url;
  return {
    id: m.id,
    // API führend: individueller Ausgabentitel aus Rothus
    title: m.issue_title?.trim() || extras?.title || m.title,
    issue_number: formatIssueNumber(m.issue_number),
    cover_image_url: m.cover_url ?? null,
    publication_date: m.published_at,
    description: m.description?.trim() || extras?.description || null,
    price_chf:
      typeof apiPrice === 'number' && Number.isFinite(apiPrice)
        ? apiPrice
        : DEFAULT_ISSUE_PRICE_CHF,
    slug: m.slug,
    embed_url: m.embed_url ?? (m.slug ? `${READER_EMBED_BASE}/${m.slug}` : null),
    reader_url: m.reader_url ?? null,
    shop_url: m.shop_url ?? null,
    editorial_title: m.issue_title?.trim() || null,
    editorial_teaser: m.teaser?.trim() || m.description?.trim() || null,
    editorial_highlights: Array.isArray(m.editorial_highlights)
      ? m.editorial_highlights.filter((text): text is string => typeof text === 'string' && !!text.trim()).map(text => text.trim()).slice(0, 4)
      : [],
    current_issue_eligible:
      m.brand === 'glide' &&
      Number.isFinite(Date.parse(m.published_at)) &&
      (!m.status || m.status === 'published') &&
      (!m.processing_status || m.processing_status === 'ready'),
    // Digitalverkauf ausschliesslich aus der zentralen API – kein lokaler Fallback.
    digital_single_price_chf: digitalEnabled ? (rawDigital as number) : null,
    digital_purchase_url: digitalEnabled ? (m.digital_purchase_url as string) : null,
  };
};

// Request-Deduplizierung innerhalb eines Seitenaufrufs + kurzer TTL-Cache (5 Min).
const CACHE_TTL_MS = 5 * 60 * 1000;
let inFlight: Promise<MagazineIssue[]> | null = null;
let cached: { data: MagazineIssue[]; at: number } | null = null;
const LAST_SUCCESS_KEY = 'glide:rothus:published-issues:v1';

/** Nur der Homepage-Ausfallpfad verwendet diesen zuletzt erfolgreichen Katalog. */
const lastSuccessfulIssues = (): MagazineIssue[] => {
  if (cached) return cached.data;
  try {
    const data: unknown = JSON.parse(localStorage.getItem(LAST_SUCCESS_KEY) || 'null');
    if (Array.isArray(data) && data.every(issue =>
      issue && typeof issue.id === 'string' && typeof issue.issue_number === 'string' &&
      typeof issue.publication_date === 'string' && issue.current_issue_eligible !== undefined
    )) return data;
  } catch { /* Storage kann deaktiviert sein. */ }
  return [];
};

export const clearRothusMagazinesCache = () => {
  inFlight = null;
  cached = null;
};

export const fetchRothusMagazines = (
  options: { fallback?: 'local' | 'last-success' } = {},
): Promise<MagazineIssue[]> => {
  const withFallback = (request: Promise<MagazineIssue[]>) => request.catch((err) => {
    console.warn('Rothus magazine API unavailable:', err);
    return options.fallback === 'last-success' ? lastSuccessfulIssues() : fallbackIssues;
  });
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return Promise.resolve(cached.data);
  }
  if (inFlight) return withFallback(inFlight);

  // Hängende Requests gelten als technischer Fehler -> lokaler Fallback.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  inFlight = fetch(ROTHUS_MAGAZINES_URL, { cache: 'no-store', signal: controller.signal })
    .then(async (res) => {
      clearTimeout(timeoutId);
      // Nur technische Fehler führen zum Fallback.
      if (!res.ok) throw new Error(`Rothus API ${res.status}`);
      const json = await res.json();
      const list = Array.isArray(json)
        ? json
        : Array.isArray((json as { magazines?: RothusMagazine[] })?.magazines)
          ? (json as { magazines: RothusMagazine[] }).magazines
          : null;
      if (!list) throw new Error('Rothus API invalid payload');
      // Eine erfolgreiche, leere Antwort ist gültig -> 0 Ausgaben.
      const data = list.map(mapMagazine);
      cached = { data, at: Date.now() };
      try { localStorage.setItem(LAST_SUCCESS_KEY, JSON.stringify(data)); } catch { /* Optionaler Ausfallcache. */ }
      inFlight = null;
      return data;
    })
    .catch((err) => {
      clearTimeout(timeoutId);
      inFlight = null;
      throw err;
    });

  return withFallback(inFlight);
};

